import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, LayoutDashboard, Search, Building2, Briefcase, Heart, ShieldCheck, User } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { dashboardPath } from "@/lib/api";
import { useState } from "react";

export const Logo = ({ className = "h-9 sm:h-11", white }) => (
  <img src={white ? "/logo-white.svg" : "/logo.svg"} alt="HomeToB - long & medium term houses" className={className} data-testid="brand-logo" />
);

export const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-xs font-bold" data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
          className={`px-3 py-1.5 rounded-full uppercase transition-colors ${lang === l ? "bg-[#142B5C] text-white" : "text-slate-500 hover:text-[#142B5C]"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

function useLinks() {
  const { t } = useLang();
  return [
    { to: "/search", label: t("nav_search"), icon: Search, id: "search" },
    { to: "/register?role=owner", label: t("nav_owners"), icon: Building2, id: "owners" },
    { to: "/register?role=tenant", label: t("nav_companies"), icon: Briefcase, id: "companies" },
  ];
}

function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const roleLabel = t(`r_${user.role}`);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1 pr-3 py-1 hover:border-[#FF6B00] transition-colors" data-testid="user-menu-btn">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[#142B5C] text-white text-sm font-bold">
            {user.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : user.name?.[0]?.toUpperCase()}
          </span>
          <span className="text-sm font-semibold text-[#142B5C] max-w-[120px] truncate">{user.name}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-slate-500">{roleLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav(dashboardPath(user))} data-testid="menu-dashboard">
          <LayoutDashboard className="mr-2 h-4 w-4" />{user.role === "admin" ? t("nav_admin") : user.role === "tenant" ? t("nav_bookings") : t("nav_dashboard")}
        </DropdownMenuItem>
        {user.role === "tenant" && (
          <DropdownMenuItem onClick={() => nav("/my-bookings?tab=favorites")} data-testid="menu-favorites"><Heart className="mr-2 h-4 w-4" />{t("nav_favorites")}</DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />{t("nav_logout")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const links = useLinks();
  const go = (to) => { setOpen(false); nav(to); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="lg:hidden grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-white" data-testid="mobile-menu-btn" aria-label="Menu">
          <Menu className="h-5 w-5 text-[#142B5C]" />
        </button>
      </SheetTrigger>
      <SheetContent aria-describedby={undefined} side="right" className="w-[86vw] max-w-sm p-0">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="p-5 border-b"><Logo className="h-10" /></div>
        <nav className="p-3 space-y-1">
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.to)} data-testid={`mobile-nav-${l.id}`}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-base font-semibold text-[#142B5C] hover:bg-orange-50">
              <l.icon className="h-5 w-5 text-[#FF6B00]" />{l.label}
            </button>
          ))}
          {user && (
            <button onClick={() => go(dashboardPath(user))} data-testid="mobile-nav-dashboard"
              className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-base font-semibold text-[#142B5C] hover:bg-orange-50">
              {user.role === "admin" ? <ShieldCheck className="h-5 w-5 text-[#FF6B00]" /> : <LayoutDashboard className="h-5 w-5 text-[#FF6B00]" />}
              {user.role === "admin" ? t("nav_admin") : user.role === "tenant" ? t("nav_bookings") : t("nav_dashboard")}
            </button>
          )}
        </nav>
        <div className="p-5 border-t space-y-3">
          <LangToggle />
          {user ? (
            <Button variant="outline" className="w-full h-12 rounded-full" onClick={async () => { await logout(); go("/"); }} data-testid="mobile-logout-btn">
              <LogOut className="mr-2 h-4 w-4" />{t("nav_logout")}
            </Button>
          ) : (
            <div className="grid gap-2">
              <Button className="h-12 rounded-full bg-[#FF6B00] hover:bg-[#E05A00]" onClick={() => go("/register")} data-testid="mobile-register-btn">{t("nav_register")}</Button>
              <Button variant="outline" className="h-12 rounded-full" onClick={() => go("/login")} data-testid="mobile-login-btn"><User className="mr-2 h-4 w-4" />{t("nav_login")}</Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Header() {
  const { user } = useAuth();
  const { t } = useLang();
  const links = useLinks();
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/95 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 sm:h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="header-logo-link"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink key={l.id} to={l.to} data-testid={`nav-${l.id}`}
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-[#142B5C] hover:bg-slate-100 transition-colors">
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><LangToggle /></div>
          {user ? (
            <div className="hidden lg:block"><UserMenu /></div>
          ) : (
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/login" className="px-4 py-2 text-sm font-semibold text-[#142B5C] hover:text-[#FF6B00]" data-testid="header-login-btn">{t("nav_login")}</Link>
              <Link to="/register" className="rounded-full bg-[#FF6B00] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#E05A00] transition-colors" data-testid="header-register-btn">{t("nav_register")}</Link>
            </div>
          )}
          <MobileMenu />
        </div>
      </div>
    </header>
  );
}
